import PropTypes from 'prop-types'
import React, {forwardRef} from 'react'
import BrightcoveVideo from '../brightcove-video'
import DirectVideo from '../direct-video'

/**
 * Video component wrapper that handles both Brightcove and direct video sources
 * Parent component can control play/pause via ref
 * @component
 * @param {Object} props - Component properties
 * @param {string} [props.title] - Video title
 * @param {Object} [props.poster] - Poster image object (for Brightcove, supports DIS; for direct, only src is used)
 * @param {boolean} [props.autoplay=false] - Whether to autoplay (will be muted)
 * @param {boolean} [props.loop] - Whether the video should be in a loop or not
 * @param {boolean} [props.videoControls=false] - Whether to show video controls (videoBar in spec)
 * @param {('eager'|'lazy')} [props.loading='lazy'] - Loading strategy for poster image
 * @param {string} props.type - Type of video: 'brightcove' or 'direct'
 * @param {string} [props.brightcoveId] - Brightcove video ID (required if type='brightcove')
 * @param {Array} [props.sources] - Direct video sources (required if type='direct')
 * @param {('animation'|'video')} [props.videoType] - Video behavior type: 'animation' (default) or 'video' (full player)
 * @param {Function} [props.onPlay] - Callback when video starts playing (used for full video mode)
 */
const Video = forwardRef(
    (
        {
            title,
            poster,
            autoplay = false,
            videoControls = false,
            loading = 'lazy',
            type: sourceType,
            videoType,
            brightcoveId,
            sources,
            loop,
            objectFit,
            widths,
            muted,
            onPlay
        },
        ref
    ) => {
        if (!sourceType) {
            console.warn('Video: type is required')
            return null
        }

        const commonProps = {
            title,
            poster,
            autoplay,
            videoControls,
            loading,
            ref,
            loop,
            muted,
            objectFit,
            videoType,
            onPlay
        }

        switch (sourceType) {
            case 'brightcove':
                return (
                    <BrightcoveVideo {...commonProps} brightcoveId={brightcoveId} widths={widths} />
                )
            case 'direct':
                return <DirectVideo {...commonProps} sources={sources} widths={widths} />
            default:
                console.warn(`Video: unsupported type "${sourceType}"`)
                return null
        }
    }
)

if (process.env.NODE_ENV === 'development') {
    Video.displayName = 'Video'
}

Video.propTypes = {
    title: PropTypes.string,
    poster: PropTypes.object,
    autoplay: PropTypes.bool,
    loop: PropTypes.bool,
    muted: PropTypes.bool,
    videoControls: PropTypes.bool,
    loading: PropTypes.oneOf(['eager', 'lazy']),
    type: PropTypes.oneOf(['brightcove', 'direct']),
    videoType: PropTypes.oneOf(['animation', 'video']),
    brightcoveId: PropTypes.string,
    sources: PropTypes.arrayOf(PropTypes.object),
    objectFit: PropTypes.string,
    widths: PropTypes.object,
    onPlay: PropTypes.func
}

export default Video
